import "bootstrap/dist/css/bootstrap.min.css";
import "./styles/global.css";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Alert, Snackbar } from "@mui/material";
import ScheduleView from "./components/ScheduleView.jsx";
import Leaderboard from "./components/Leaderboard.jsx";
import Footer from "./components/Footer.jsx";
import Header from "./components/Header.jsx";
import { useScoreboard } from "./context/NCAAFDataContext";
import { Amplify } from "aws-amplify";
import config from "./amplifyconfiguration.json";
import PickForm from "./components/PickForm.jsx";
import Home from "./components/Home.jsx";
import { fetchPicks } from "./utils/fetchPicks";
import AllPicks from "./components/AllPicks.jsx";
import WinnersPodium from "./components/WinnersPodium.jsx";
import { AWS_DISABLED } from "./constants/appFlags";

if (!AWS_DISABLED) {
  Amplify.configure(config);
}

const App = () => {
  const isBowlSeason = false; // set this to true when in season
  const [currentPage, setCurrentPage] = useState("home");
  const [playerPicks, setPlayerPicks] = useState([]);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [picksLoading, setPicksLoading] = useState(false);
  const [isLocked, setLocked] = useState(true);
  const { games: scoreboardGames } = useScoreboard();
  const gamesStarted = scoreboardGames.some((game) =>
    Object.values(game).some((state) => ["in", "post"].includes(state))
  );

  const handleToastClose = (event, reason) => {
    if (reason === "clickaway") return; // keep it open unless explicit close or timeout
    setToast((prev) => ({ ...prev, open: false }));
  };

  // Build matchups from live scoreboard data (keeps pick keys aligned with PickForm)
  const matchups = useMemo(() => {
    const seen = {};
    return (scoreboardGames || []).map((game, index) => {
      const bowlName = game?.bowl || "Bowl Game";
      const count = (seen[bowlName] || 0) + 1;
      seen[bowlName] = count;
      const pickKey = count > 1 ? `${bowlName} (#${count})` : bowlName;

      const homeScore = Number(game?.home?.score);
      const awayScore = Number(game?.away?.score);
      const scoresValid = !Number.isNaN(homeScore) && !Number.isNaN(awayScore);
      let winnerAbbr = "";
      if (scoresValid && (game?.isFinal || game?.state === "post")) {
        if (homeScore > awayScore) winnerAbbr = game?.home?.abbr || "";
        else if (awayScore > homeScore) winnerAbbr = game?.away?.abbr || "";
      }

      return {
        id: game?.id || `game-${index}`,
        game: bowlName,
        pickKey,
        team1: game?.home?.displayName || game?.home?.abbr || "Home",
        team2: game?.away?.displayName || game?.away?.abbr || "Away",
        winner: winnerAbbr,
        date: game?.startTimeText || `${index}`,
        gameTotal: homeScore + awayScore,
      };
    });
  }, [scoreboardGames]);

  // Load picks from the GraphQL API to keep everything in sync
  const loadPicks = useCallback(async () => {
    if (matchups.length === 0) return;
    if (AWS_DISABLED) {
      setPlayerPicks([]);
      setPicksLoading(false);
      return;
    }
    setPicksLoading(true);
    try {
      const picksResponse = await fetchPicks();
      const submissions =
        picksResponse?.data?.listSubmissions?.items?.filter(Boolean) || [];

      const normalizedPicks = submissions.map((submission) => {
        let parsedPicks = {};
        try {
          parsedPicks = JSON.parse(submission?.picks || "{}");
        } catch (err) {
          parsedPicks = {};
        }

        const picksArray = matchups.map((game) => {
          const bowlKey = game?.pickKey || game?.game?.trim() || "No Bowl Game";
          return parsedPicks?.[bowlKey] || "-";
        });

        return {
          name: submission?.name?.trim() || "Unnamed Entry",
          picks: picksArray,
          tiebreaker: submission?.tieBreaker,
        };
      });
      setPlayerPicks(normalizedPicks);
    } catch (err) {
      console.error("Failed to load picks:", err);
      setToast({
        open: true,
        severity: "error",
        message: "Unable to load picks from the database.",
      });
    } finally {
      setPicksLoading(false);
    }
  }, [matchups, AWS_DISABLED]);

  useEffect(() => {
    loadPicks();
  }, [loadPicks]);

  const loginHelper = (value) => {
    if (value) {
      setLocked(false);
      setToast((prev) => ({ ...prev, open: false })); // close any stale toasts
    } else {
      setLocked(true);
      setToast({
        open: true,
        severity: "error",
        message: "Please enter the correct login info.",
      });
    }
  };

  return (
    <div className="container">
      {isBowlSeason ? (
        <>
          <title>College Bowl Game Picks 🏆</title>

          <Header
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isLocked={isLocked}
            gamesStarted={gamesStarted}
          />
          <div className="content">
            {currentPage === "home" && (
              <Home
                onNavigate={setCurrentPage}
                isLocked={isLocked}
                loginHelper={loginHelper}
                gamesStarted={gamesStarted}
              />
            )}
            {currentPage === "schedule-view" && (
              <ScheduleView playerPicks={playerPicks} matchups={matchups} />
            )}
            {currentPage === "all-picks" && (
              <AllPicks playerPicks={playerPicks} matchups={matchups} />
            )}
            {currentPage === "leaderboard" && (
              <Leaderboard
                playerPicks={playerPicks}
                matchups={matchups}
                loading={picksLoading}
              />
            )}
            {currentPage === "picks" && (
              <PickForm
                playerPicks={playerPicks}
                matchups={matchups}
                onSubmitResult={(result) => {
                  if (!result) return;
                  setToast({ open: true, ...result });
                  if (result.severity === "success") {
                    loadPicks();
                    setCurrentPage("leaderboard");
                  }
                }}
              />
            )}
          </div>
          <Footer />
          <Snackbar
            open={toast.open}
            autoHideDuration={20000}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            sx={{ mb: 2 }}
            onClose={handleToastClose}
          >
            <Alert
              elevation={6}
              variant="filled"
              severity={toast.severity}
              onClose={handleToastClose}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </>
      ) : (
        // Display winners in the offseason
        <WinnersPodium />
      )}
    </div>
  );
};

export default App;
